import { useState, useRef } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { faqItems } from '@/mocks/homeData';

export default function FAQSection() {
  const [openId, setOpenId] = useState<number | null>(null);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });

  const toggleFaq = (id: number) => {
    setOpenId(openId === id ? null : id);
  };

  return (
    <section ref={ref} className="relative bg-background-50 py-24 md:py-32 lg:py-40 overflow-hidden">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="max-w-[800px] mx-auto">
          {/* Header */}
          <div className="text-center mb-16 md:mb-20">
            <motion.span
              initial={{ opacity: 0, y: 10 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6 }}
              className="text-xs tracking-[0.2em] uppercase text-primary-400 font-label"
            >
              Dúvidas
            </motion.span>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="font-heading text-4xl md:text-5xl lg:text-6xl text-foreground-950 mt-4 leading-[1.1]"
            >
              Perguntas<br />
              <span className="italic font-light">Frequentes.</span>
            </motion.h2>
          </div>

          {/* FAQ Items */}
          <div className="space-y-3">
            {faqItems.map((item, index) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.4, delay: 0.1 + index * 0.06 }}
                className={`border rounded-lg overflow-hidden bg-background-50 transition-all duration-300 ${
                  openId === item.id
                    ? 'border-primary-200/60 shadow-[0_8px_30px_-12px_rgba(0,0,0,0.04)]'
                    : 'border-background-200/70 hover:border-background-300/70'
                }`}
              >
                <button
                  onClick={() => toggleFaq(item.id)}
                  className="w-full flex items-center justify-between gap-4 p-5 md:p-6 text-left transition-colors duration-300 hover:bg-background-100/50"
                >
                  <span className="font-label text-sm md:text-base text-foreground-800 font-medium pr-4">
                    {item.question}
                  </span>
                  <motion.span
                    animate={{ rotate: openId === item.id ? 45 : 0 }}
                    transition={{ duration: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
                    className="w-6 h-6 flex items-center justify-center flex-shrink-0 text-foreground-400"
                  >
                    <i className="ri-add-line text-lg"></i>
                  </motion.span>
                </button>

                <AnimatePresence initial={false}>
                  {openId === item.id && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.35, ease: [0.25, 0.1, 0.25, 1] }}
                      className="overflow-hidden"
                    >
                      <div className="px-5 md:px-6 pb-5 md:pb-6">
                        <div className="h-[1px] bg-background-200/60 mb-4" />
                        <p className="text-sm text-foreground-500 leading-relaxed">
                          {item.answer}
                        </p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}